import { Box, Grid, styled, Typography } from '@mui/material';

import { DateTime } from 'luxon';
import { isToday } from '../../../../../../../../../shared/utils/date-utils';
import { useGetVisibleDays } from '../../hooks/use-get-visible-days';

export const CalendarPlanningHeader = () => {
  const { visibleDays } = useGetVisibleDays();
  const weekDayAbreviation = (d: string) => DateTime.fromISO(d).toFormat('ccc');
  const monthNumber = (d: string) => DateTime.fromISO(d).toFormat('dd');
  const dayCount = visibleDays.length;

  return (
    <StyledHeader mt={8}>
      <StyledWeekDaysGrid container flexWrap={'nowrap'}>
        {visibleDays.map((columnDate) => (
          <StyledDayHead dayCount={dayCount} item key={columnDate}>
            <StyledLozenge isToday={isToday(columnDate)}>
              <StyledWeekDay variant="inherit">{weekDayAbreviation(columnDate)}</StyledWeekDay>
              <StyledMonthNumber variant="inherit">{monthNumber(columnDate)}</StyledMonthNumber>
            </StyledLozenge>
          </StyledDayHead>
        ))}
      </StyledWeekDaysGrid>
    </StyledHeader>
  );
};

const StyledHeader = styled(Box)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  background: theme.palette.lwWhite['100'],
  zIndex: 1,
  gap: theme.spacing(8),
  '& .MuiGrid-item:nth-child(2n - 1)': {
    borderTopRightRadius: '1rem',
    borderTopLeftRadius: '1rem',
  },
}));

const StyledWeekDaysGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingLeft: '14vw',
}));

const StyledDayHead = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'dayCount',
})<{
  dayCount: number;
}>(({ theme, dayCount }) => ({
  textTransform: 'capitalize',
  width: `calc(100% / ${dayCount})`,
  padding: theme.spacing(2),
  '&:last-child': {
    borderRight: 'none',
  },
}));

const StyledLozenge = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isToday',
})<{
  isToday: boolean;
}>(({ theme, isToday }) => ({
  padding: '1rem 1.5rem',
  gap: '0.75rem',
  borderRadius: '4.125rem',
  width: 'fit-content',
  display: 'flex',
  alignItems: 'center',
  margin: '0 auto',
  color: isToday ? theme.palette.lwPrimaryAccentLight[100] : undefined,
  background: isToday ? theme.palette.lwPrimaryAccentLight[10] : undefined,
  '& > *': {
    opacity: isToday ? 1 : undefined,
  },
}));

const StyledMonthNumber = styled(Typography)(() => ({
  fontWeight: 'bolder',
  lineHeight: 'normal',
  letterSpacing: '0.025rem',
  fontSize: '16px',
}));

const StyledWeekDay = styled(Typography)(() => ({
  textTransform: 'uppercase',
  opacity: 0.4,
  fontSize: '12px',
}));
